import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { LANGS } from "../i18n";
import { Headset, Plus, Trash2, KeyRound, Ban, CheckCircle2, Users, Gauge, RotateCcw, Database } from "lucide-react";
import TierRoster from "../components/TierRoster";

export default function SupportConsole() {
  const [users, setUsers] = useState([]);
  const [seats, setSeats] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ username: "", password: "", display_name: "", language: "ENG", permissions: {} });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [actData, setActData] = useState(null);

  const openActivity = async (u) => {
    try { const { data } = await client.get(`/admin/support-users/${u.id}/activity`); setActData(data); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const fmt = (t) => t ? new Date(t).toLocaleString() : "—";
  const [resettingAll, setResettingAll] = useState(false);
  const [dsOpen, setDsOpen] = useState(false);
  const [ds, setDs] = useState(null);
  const [dsFields, setDsFields] = useState(null);
  const [dsCustom, setDsCustom] = useState(false);

  const openDataset = async () => {
    try {
      const { data } = await client.get("/admin/support/dataset");
      setDs(data.dataset); setDsFields(data.fields); setDsCustom(data.custom); setDsOpen(true);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const ENT_LABELS = { persons: "People (Family Tree)", currencies: "Currency pools", holdings: "Holdings", benefits: "Benefits" };
  const addRow = (ent) => {
    const f = dsFields[ent]; const row = {};
    f.str.forEach((k) => row[k] = ""); f.num.forEach((k) => row[k] = 0);
    setDs({ ...ds, [ent]: [...ds[ent], row] });
  };
  const setCell = (ent, i, k, v) => setDs({ ...ds, [ent]: ds[ent].map((r, j) => j === i ? { ...r, [k]: v } : r) });
  const rmRow = (ent, i) => setDs({ ...ds, [ent]: ds[ent].filter((_, j) => j !== i) });
  const saveDataset = async () => {
    try { const { data } = await client.put("/admin/support/dataset", ds); setDs(data.dataset); setDsCustom(true); setMsg("Demo dataset saved. Use “Reset all sandboxes” to apply it to existing seats."); setDsOpen(false); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const useDefaults = async () => {
    try { const { data } = await client.delete("/admin/support/dataset"); setDs(data.dataset); setDsCustom(false); setMsg("Reverted to the built-in default demo dataset."); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const resetAll = async () => {
    if (!window.confirm("Push the standard demo dataset to ALL support seats? Each staffer's current sandbox edits will be discarded.")) return;
    setResettingAll(true);
    try { const { data } = await client.post("/admin/support/reset-all-sandboxes"); setMsg(`Reset ${data.reset} sandbox(es) to the standard demo dataset.`); }
    catch (e) { setMsg(apiErr(e)); }
    setResettingAll(false);
  };

  const load = () => {
    client.get("/admin/support-users").then((r) => setUsers(r.data)).catch(() => {});
    client.get("/admin/support/seat-usage").then((r) => setSeats(r.data)).catch(() => {});
  };
  useEffect(() => {
    client.get("/admin/support/perms").then((r) => { setCatalog(r.data); setForm((f) => ({ ...f, permissions: { ...r.data.defaults } })); }).catch(() => {});
    load();
    const id = setInterval(() => client.get("/admin/support/seat-usage").then((r) => setSeats(r.data)).catch(() => {}), 15000);
    return () => clearInterval(id);
  }, []);

  const create = async () => {
    setMsg("");
    try {
      await client.post("/admin/support-users", form);
      setOpen(false); setForm({ username: "", password: "", display_name: "", language: "ENG", permissions: { ...catalog.defaults } });
      load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const togglePerm = async (u, key) => {
    const permissions = { ...u.permissions, [key]: !u.permissions[key] };
    setUsers((us) => us.map((x) => x.id === u.id ? { ...x, permissions } : x));
    await client.put(`/admin/support-users/${u.id}`, { permissions: { [key]: permissions[key] } }).catch(() => load());
  };
  const changeLang = async (u, language) => {
    await client.put(`/admin/support-users/${u.id}`, { language }).catch(() => {});
    load();
  };
  const setDisabled = async (u, disabled) => { await client.post(`/admin/support-users/${u.id}/set-disabled`, { disabled }); load(); };
  const resetPw = async () => {
    try { await client.post(`/admin/support-users/${pwFor}/reset-password`, { password: newPw }); setPwFor(null); setNewPw(""); setMsg("Password reset; active sessions signed out."); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (u) => { if (window.confirm(`Delete ${u.username} and wipe their sandbox?`)) { await client.delete(`/admin/support-users/${u.id}`); load(); } };

  const langName = (c) => LANGS.find((l) => l.code === c)?.native || c;
  const usedLangs = seats ? Object.entries(seats.per_language).filter(([, n]) => n > 0) : [];

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Headset size={26} /> Support Console</h1>
        <span className="opacity-60">Sandbox seats &amp; delegated Master Enabler functions</span>
        <div className="ms-auto flex gap-2">
          <Button variant="ghost" onClick={openDataset} data-testid="open-dataset-editor"><Database size={16} /> Demo dataset</Button>
          <Button variant="ghost" onClick={resetAll} disabled={resettingAll || users.length === 0} data-testid="reset-all-sandboxes">
            <RotateCcw size={16} className={resettingAll ? "animate-spin" : ""} /> {resettingAll ? "Resetting…" : "Reset all sandboxes"}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="open-create-support"><Plus size={16} /> New support user</Button>
        </div>
      </div>

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="support-message">{msg}</Card>}

      <TierRoster />

      {seats && (
        <Card data-testid="seat-usage" className="space-y-3">
          <div className="flex items-center gap-2"><Gauge size={18} className="text-[var(--primary)]" /><span className="font-head text-lg">Live seat usage</span>
            <span className="ms-auto text-sm">Total <b data-testid="seat-total">{seats.total}</b> / {seats.cap_total} concurrent</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
            <div className="h-full bg-[var(--primary)] transition-all" style={{ width: `${Math.min(100, (seats.total / seats.cap_total) * 100)}%` }} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {usedLangs.length === 0 && <div className="text-sm opacity-50">No active sessions. Up to {seats.cap_per_language} concurrent per language.</div>}
            {usedLangs.map(([code, n]) => (
              <div key={code} className="text-xs" data-testid={`seat-lang-${code}`}>
                <div className="mb-0.5 flex justify-between"><span>{langName(code)} ({code})</span><span>{n}/{seats.cap_per_language}</span></div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                  <div className={`h-full ${n >= seats.cap_per_language ? "bg-[#DA1E28]" : "bg-[#24A148]"}`} style={{ width: `${Math.min(100, (n / seats.cap_per_language) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 text-sm opacity-70"><Users size={16} /> {users.length} support user(s)</div>
      <div className="grid gap-4">
        {users.length === 0 && <Card className="text-center opacity-60">No support users yet. Create one to begin.</Card>}
        {users.map((u) => (
          <Card key={u.id} className="space-y-3" data-testid={`support-user-${u.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-head font-medium">{u.name}</span>
              <Badge color="info">@{u.username}</Badge>
              <Badge color="neutral">{langName(u.language)} · {u.language}</Badge>
              {u.active_sessions > 0 ? <Badge color="success">{u.active_sessions} online</Badge> : <Badge color="neutral">offline</Badge>}
              {u.disabled ? <Badge color="danger">suspended</Badge> : <Badge color="success">active</Badge>}
              <span className="text-xs opacity-50" data-testid={`last-login-${u.id}`}>last login {fmt(u.last_login)}</span>
              <div className="ms-auto flex flex-wrap gap-2">
                <Select value={u.language} onChange={(e) => changeLang(u, e.target.value)} data-testid={`lang-${u.id}`} className="!py-1 text-xs">
                  {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}`}</option>)}
                </Select>
                <Button variant="ghost" onClick={() => openActivity(u)} data-testid={`activity-${u.id}`}><Gauge size={14} /> Activity</Button>
                <Button variant="ghost" onClick={() => { setPwFor(u.id); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}><KeyRound size={14} /> Reset PW</Button>
                {u.disabled
                  ? <Button variant="subtle" onClick={() => setDisabled(u, false)} data-testid={`restore-${u.id}`}><CheckCircle2 size={14} /> Restore</Button>
                  : <Button variant="subtle" onClick={() => setDisabled(u, true)} data-testid={`suspend-${u.id}`}><Ban size={14} /> Suspend</Button>}
                <Button variant="danger" onClick={() => del(u)} data-testid={`delete-support-${u.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
            {catalog && (
              <div className="flex flex-wrap gap-2 border-t border-black/10 dark:border-white/10 pt-3">
                {catalog.perms.map((p) => (
                  <button key={p} onClick={() => togglePerm(u, p)} data-testid={`perm-${u.id}-${p}`}
                    className={`rounded-full px-3 py-1 text-xs lift ${u.permissions[p] ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15 opacity-70"}`}>
                    {catalog.labels[p]}
                  </button>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New support user" testid="create-support-modal">
        <div className="space-y-3">
          <Input label="Username (login)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="cs-username" />
          <Input label="Temporary password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cs-password" />
          <Input label="Display name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="cs-name" />
          <Select label="Language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="cs-language">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}`}</option>)}
          </Select>
          {catalog && (
            <div className="space-y-2 rounded-lg border border-black/10 dark:border-white/10 p-3">
              <div className="text-xs font-medium opacity-70">Delegated Master Enabler functions</div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {catalog.perms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.permissions[p]} data-testid={`cs-perm-${p}`}
                      onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [p]: e.target.checked } })} />
                    {catalog.labels[p]}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.username || !form.password || !form.display_name} data-testid="submit-support">Create &amp; seed sandbox</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!pwFor} onClose={() => setPwFor(null)} title="Reset support password" testid="reset-pw-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Setting a new password signs the staffer out of all active sessions.</p>
          <Input label="New password" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-pw-input" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPwFor(null)}>Cancel</Button><Button onClick={resetPw} disabled={newPw.length < 6} data-testid="submit-reset-pw">Reset</Button></div>
        </div>
      </Modal>

      <Modal open={dsOpen} onClose={() => setDsOpen(false)} title="Standard demo dataset" testid="dataset-modal">
        <div className="space-y-4">
          <p className="text-sm opacity-70">Define the curated demo records seeded into every support sandbox. {dsCustom ? <Badge color="success">custom</Badge> : <Badge color="neutral">using defaults</Badge>}</p>
          {ds && dsFields && Object.keys(ENT_LABELS).map((ent) => (
            <div key={ent} className="rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid={`ds-section-${ent}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="font-head text-sm">{ENT_LABELS[ent]}</span>
                <Button variant="ghost" className="ms-auto !py-1 text-xs" onClick={() => addRow(ent)} data-testid={`ds-add-${ent}`}><Plus size={13} /> Add</Button>
              </div>
              <div className="space-y-2">
                {ds[ent].length === 0 && <div className="text-xs opacity-50">No rows.</div>}
                {ds[ent].map((row, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`ds-row-${ent}-${i}`}>
                    {[...dsFields[ent].str, ...dsFields[ent].num].map((k) => (
                      <input key={k} placeholder={k} value={row[k] ?? ""}
                        type={dsFields[ent].num.includes(k) ? "number" : "text"}
                        onChange={(e) => setCell(ent, i, k, e.target.value)}
                        data-testid={`ds-${ent}-${i}-${k}`}
                        className="w-28 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-xs" />
                    ))}
                    <button onClick={() => rmRow(ent, i)} data-testid={`ds-rm-${ent}-${i}`} className="text-[#DA1E28] opacity-70 hover:opacity-100"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={useDefaults} data-testid="ds-use-defaults">Use built-in defaults</Button>
            <Button variant="ghost" onClick={() => setDsOpen(false)}>Cancel</Button>
            <Button onClick={saveDataset} data-testid="ds-save">Save dataset</Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!actData} onClose={() => setActData(null)} title="Support activity" testid="activity-modal">
        {actData && (
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-head">{actData.name} <span className="opacity-60">@{actData.username}</span></div>
              <div className="opacity-60">Last login: {fmt(actData.last_login)} · {actData.total} action(s) logged</div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium opacity-70">Functions exercised</div>
              {Object.keys(actData.summary).length === 0 && <div className="text-sm opacity-50">No delegated functions used yet.</div>}
              <div className="flex flex-wrap gap-2">
                {Object.entries(actData.summary).map(([k, n]) => (
                  <span key={k} className="rounded-full bg-[var(--primary)]/10 px-3 py-1 text-xs" data-testid={`act-sum-${k}`}>
                    {actData.labels[k] || k} <b>×{n}</b>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium opacity-70">Recent events</div>
              <div className="max-h-72 space-y-1 overflow-y-auto" data-testid="activity-events">
                {actData.events.length === 0 && <div className="text-sm opacity-50">Nothing yet.</div>}
                {actData.events.map((e, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-black/5 dark:border-white/5 py-1 text-xs">
                    <span>{actData.labels[e.action] || e.action}</span>
                    <span className="opacity-50">{fmt(e.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
